import { Routes } from '@angular/router';
import { LoginComponent } from './modules/base/login/login.component';
import { TokenGuard } from './guards/token.guard';
import { LogoutComponent } from './modules/base/logout/logout.component';
import { EraLoginComponent } from './modules/base/era-login/era-login.component';
import { SsoLoginComponent } from './modules/base/sso-login/sso-login.component';
import { PermissionGuard } from './guards/permission.guard';
import { UnauthorizedComponent } from './modules/base/unauthorized/unauthorized.component';
import { ProfileComponent } from './modules/user/profile/profile.component';
import { HomeComponent } from './modules/base/home/home.component';
import { ChangePasswordComponent } from './modules/base/change-password/change-password.component';
import { StimulationFormPrintComponent } from './modules/ivf/stimulation-form/stimulation-form-print/stimulation-form-print.component';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    data: {
      leftNavi: 'login',
      rightNavi: 'login'
    }
  },
  {
    path: 'login/:orgSlug',
    component: SsoLoginComponent,
    data: {
      leftNavi: 'login',
      rightNavi: 'login',
      loginEvent: 'start'
    }
  },
  {
    path: 'login/:orgSlug/callback',
    component: SsoLoginComponent,
    data: {
      leftNavi: 'login',
      rightNavi: 'login',
      loginEvent: 'confirm'
    }
  },
  {
    path: 'era/login',
    component: EraLoginComponent,
    data: {
      leftNavi: 'login',
      rightNavi: 'login',
      loginEvent: 'login'
    }
  },
  {
    path: 'era/confirm',
    component: EraLoginComponent,
    data: {
      leftNavi: 'login',
      rightNavi: 'login',
      loginEvent: 'confirm'
    }
  },
  {
    path: 'era/unlock',
    component: EraLoginComponent,
    data: {
      leftNavi: 'login',
      rightNavi: 'login',
      loginEvent: 'unlock'
    }
  },
  {
    path: 'logout',
    component: LogoutComponent
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
    data: {
      breadCrumbs: {
        order: 0
      }
    }
  },
  {
    path: 'print',
    data: {
      leftNavi: 'login'
    },
    children: [
      {
        path: 'stimulationForm/:treatmentId',
        component: StimulationFormPrintComponent
      }
    ]
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [TokenGuard],
    loadChildren: () =>
      import('./modules/user/user.routes').then((m) => m.USER_ROUTES),
    data: {
      leftNavi: 'top'
    }
  },
  {
    path: 'change/password',
    component: ChangePasswordComponent,
    canActivate: [TokenGuard],
    data: {
      breadCrumbs: {
        order: 10
      }
    }
  },
  {
    path: 'customer',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      permission: {
        accessOnly: 'read_customer',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/customer/customer.routes').then(
        (m) => m.CUSTOMER_ROUTES
      )
  },
  {
    path: 'donor',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      permission: {
        accessOnly: 'read_donor',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/donor/donor.routes').then((m) => m.DONOR_ROUTES)
  },
  {
    path: 'settings',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'settings',
      permission: {
        accessOnly: 'read_setting',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/settings/settings.routes').then(
        (m) => m.SETTINGS_ROUTES
      )
  },
  {
    path: 'schedule',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      permission: {
        accessOnly: 'read_schedule',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/schedule/schedule.routes').then(
        (m) => m.SCHEDULE_ROUTES
      )
  },
  {
    path: 'week-plan',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      permission: {
        accessOnly: 'read_weekplan',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/week-plan/week-plan.routes').then(
        (m) => m.WEEK_PLAN_ROUTES
      )
  },
  {
    path: 'results',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      permission: {
        accessOnly: 'read_schedule',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/result/result.routes').then((m) => m.RESULT_ROUTES)
  },
  {
    path: 'invoices',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      permission: {
        accessOnly: 'read_invoice',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/invoices/invoices.routes').then(
        (m) => m.INVOICES_ROUTES
      )
  },
  {
    path: 'statistics',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      permission: {
        accessOnly: 'read_statistics',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/statistics/statistics.routes').then(
        (m) => m.STATISTICS_ROUTES
      )
  },
  {
    path: 'donated-embryos',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top',
      standalone: true,
      permission: {
        accessOnly: 'read_donated_embryos',
        redirectTo: '/unauthorized'
      }
    },
    canLoad: [PermissionGuard],
    loadChildren: () =>
      import('./modules/donated-embryos/donated-embryos.routes').then(
        (m) => m.DONATED_EMBRYOS_ROUTES
      )
  },
  {
    path: 'clinic-management',
    canActivate: [TokenGuard],
    // No section-wide privilege: Klinikan hallinta collects pages that each
    // carry their own privilege (Lab management, Pakastushallinta, ...), so a
    // section gate would let one page's privilege govern all of them. Every
    // child enforces its own via PermissionGuard in canActivate.
    data: {
      leftNavi: 'top'
    },
    loadChildren: () =>
      import('./modules/clinic-management/clinic-management.routes').then(
        (m) => m.CLINIC_MANAGEMENT_ROUTES
      )
  },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [TokenGuard]
  },
  {
    path: 'help',
    canActivate: [TokenGuard],
    data: {
      leftNavi: 'top'
    },
    loadChildren: () =>
      import('./modules/help/help.routes').then((m) => m.HELP_ROUTES)
  },
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: '/login'
  }
];
